import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Button,
  Stack,
  Tooltip
} from '@mui/material';
import {
  Backup as BackupIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Storage as StorageIcon,
  Image as ImageIcon,
  CloudDownload as CloudDownloadIcon,
  Add as AddIcon,
  CleaningServices as CleaningServicesIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import {
  fetchBackups,
  fetchBackupStats,
  createBackup,
  deleteBackup,
  cleanupOldBackups,
  downloadFullBackup,
  downloadDatabase,
  downloadImages
} from '../api/backupApi';

const BackupManagement = () => {
  const { t } = useTranslation();
  const [backups, setBackups] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [backupsData, statsData] = await Promise.all([
        fetchBackups(),
        fetchBackupStats()
      ]);
      setBackups(backupsData);
      setStats(statsData);
    } catch (error) {
      console.error('❌ Error loading backups:', error);
      Swal.fire(
        t('Error') || 'Error',
        t('BackupLoadFail') || 'Failed to load backups',
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBackup = async () => {
    const result = await Swal.fire({
      title: t('CreateBackup') || 'Create New Backup?',
      text: t('CreateBackupConfirm') || 'This will create a complete backup of your database and images. This may take a few minutes.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: t('Yes_Create') || 'Yes, create it!',
      cancelButtonText: t('Cancel') || 'Cancel'
    });

    if (result.isConfirmed) {
      setCreating(true);
      try {
        await createBackup();
        Swal.fire(
          t('Success') || 'Success!',
          t('BackupCreatedSuccess') || 'Backup created successfully',
          'success'
        );
        loadData();
      } catch (error) {
        Swal.fire(
          t('Error') || 'Error!',
          t('BackupCreateFail') || 'Failed to create backup',
          'error'
        );
      } finally {
        setCreating(false);
      }
    }
  };

  const handleDelete = async (backupName) => {
    const result = await Swal.fire({
      title: t('Are_You_Sure') || 'Are you sure?',
      text: t('DeleteBackupWarning') || 'This backup will be permanently deleted!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: t('Yes_Delete_It') || 'Yes, delete it!',
      cancelButtonText: t('Cancel') || 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        await deleteBackup(backupName);
        Swal.fire(
          t('Deleted') || 'Deleted!',
          t('BackupDeletedSuccess') || 'Backup has been deleted.',
          'success'
        );
        loadData();
      } catch (error) {
        Swal.fire(
          t('Error') || 'Error!',
          t('BackupDeleteFail') || 'Failed to delete backup.',
          'error'
        );
      }
    }
  };

  const handleCleanup = async () => {
    const result = await Swal.fire({
      title: t('CleanupOldBackups') || 'Cleanup Old Backups?',
      text: t('CleanupWarning') || 'This will delete all backups older than 6 months.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ff9800',
      cancelButtonColor: '#3085d6',
      confirmButtonText: t('Yes_Cleanup') || 'Yes, cleanup!',
      cancelButtonText: t('Cancel') || 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        await cleanupOldBackups(6);
        Swal.fire(
          t('Success') || 'Success!',
          t('CleanupSuccess') || 'Old backups cleaned up successfully',
          'success'
        );
        loadData();
      } catch (error) {
        Swal.fire(
          t('Error') || 'Error!',
          t('CleanupFail') || 'Failed to cleanup backups',
          'error'
        );
      }
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Statistics Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
        <Card sx={{ bgcolor: 'primary.main', color: 'white' }}>
          <CardContent>
            <Typography variant="subtitle2" sx={{ opacity: 0.8 }}>
              {t('TotalBackups') || 'Total Backups'}
            </Typography>
            <Typography variant="h3" fontWeight="bold">
              {stats.totalBackups || 0}
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ bgcolor: 'success.main', color: 'white' }}>
          <CardContent>
            <Typography variant="subtitle2" sx={{ opacity: 0.8 }}>
              {t('TotalSize') || 'Total Size'}
            </Typography>
            <Typography variant="h4" fontWeight="bold">
              {stats.totalSizeFormatted || '0 MB'}
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ bgcolor: 'info.main', color: 'white' }}>
          <CardContent>
            <Typography variant="subtitle2" sx={{ opacity: 0.8 }}>
              {t('OldestBackup') || 'Oldest Backup'}
            </Typography>
            <Typography variant="h6" fontWeight="bold">
              {stats.oldestBackup?.replace('backup_', '') || 'N/A'}
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ bgcolor: 'warning.main', color: 'white' }}>
          <CardContent>
            <Typography variant="subtitle2" sx={{ opacity: 0.8 }}>
              {t('LatestBackup') || 'Latest Backup'}
            </Typography>
            <Typography variant="h6" fontWeight="bold">
              {stats.newestBackup?.replace('backup_', '') || 'N/A'}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Main Card */}
      <Card>
        <CardHeader
          title={t('BackupManagement') || 'Backup Management'}
          subheader={t('BackupManagementDesc') || 'Manage database and file system backups'}
          avatar={<BackupIcon color="primary" fontSize="large" />}
          action={
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                color="primary"
                startIcon={creating ? <CircularProgress size={20} color="inherit" /> : <AddIcon />}
                onClick={handleCreateBackup}
                disabled={creating}
              >
                {creating ? t('Creating') || 'Creating...' : t('CreateBackup') || 'Create Backup'}
              </Button>
              <Button
                variant="outlined"
                color="warning"
                startIcon={<CleaningServicesIcon />}
                onClick={handleCleanup}
              >
                {t('Cleanup') || 'Cleanup'}
              </Button>
              <IconButton color="primary" onClick={loadData}>
                <RefreshIcon />
              </IconButton>
            </Stack>
          }
          sx={{ bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}
        />
        <CardContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('BackupName') || 'Backup Name'}</TableCell>
                    <TableCell>{t('Date') || 'Date'}</TableCell>
                    <TableCell>{t('Size') || 'Size'}</TableCell>
                    <TableCell>{t('Created') || 'Created'}</TableCell>
                    <TableCell>{t('Status') || 'Status'}</TableCell>
                    <TableCell align="right">{t('Actions') || 'Actions'}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {backups.map((backup) => (
                    <TableRow
                      key={backup.name}
                      sx={{
                        bgcolor: 'rgba(33, 150, 243, 0.05)',
                        '&:hover': {
                          bgcolor: 'rgba(33, 150, 243, 0.1)'
                        }
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <BackupIcon color="primary" />
                          <Typography fontWeight={600}>
                            {backup.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {backup.date}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={backup.sizeFormatted}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(backup.created).toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={t('Ready') || 'Ready'}
                          color="success"
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Tooltip title={t('DownloadFull') || 'Download Full Backup'}>
                            <IconButton
                              color="primary"
                              size="small"
                              onClick={() => downloadFullBackup(backup.name)}
                            >
                              <CloudDownloadIcon />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title={t('DownloadDatabase') || 'Download Database Only'}>
                            <IconButton
                              color="info"
                              size="small"
                              onClick={() => downloadDatabase(backup.name)}
                            >
                              <StorageIcon />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title={t('DownloadImages') || 'Download Images Only'}>
                            <IconButton
                              color="secondary"
                              size="small"
                              onClick={() => downloadImages(backup.name)}
                            >
                              <ImageIcon />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title={t('Delete') || 'Delete'}>
                            <IconButton
                              color="error"
                              size="small"
                              onClick={() => handleDelete(backup.name)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                  {backups.length === 0 && !loading && (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Box sx={{ py: 4 }}>
                          <BackupIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                          <Typography variant="h6" color="text.secondary">
                            {t('NoBackupsFound') || 'No backups found'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {t('CreateFirstBackup') || 'Create your first backup to get started'}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default BackupManagement;